import {Epic} from 'redux-observable';
import { EMPTY, concat, from, interval, of } from 'rxjs';
import {isActionOf} from 'typesafe-actions';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import {RootAction, RootState, Services} from '../root-types';

import {
    getAllTreeAsync,
    getBannerHostPortAsync,
    getFavoritesTreeAsync,
    getMarketLimitAsync,
    getNotCalculatedStepsAsync,
    getOneFixtureAsync,
    getPlayerInfoAsync,
    getPlayerSettings,
    getSportsAsync,
    getStepsAsync,
    getTreeFilterAsync,
    getTreeLiveAsync,
    getTreeRatingAsync,
    getTreeResultAsync,
    saveCoupon,
    savePlayerSettingsAsync,
    startTreePolling,
    stopTreePolling
} from './actions';
import { TreeType } from './types';
import { FlatTree } from '../../models/api/codegen/code';


export const loadOneFixtureEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getOneFixtureAsync.request)),
        switchMap(action => from(api.getFixture(action.payload))
            .pipe(map(getOneFixtureAsync.success), catchError((message: string) => of(getOneFixtureAsync.failure(message)))))
    );
};

export const loadSportsEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getSportsAsync.request)),
        switchMap(action => from(api.getSports(action.payload))
            .pipe(map(getSportsAsync.success), catchError((message: string) => of(getSportsAsync.failure(message)))))
    );
};


export const loadStepsEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getStepsAsync.request)),
        switchMap(action => from(api.getSteps(action.payload))
            .pipe(map(getStepsAsync.success), catchError((message: string) => of(getStepsAsync.failure(message)))))
    );
};

export const loadNotCalculatedStepsEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getNotCalculatedStepsAsync.request)),
        switchMap(action => from(api.getNotCalculatedSteps(action.payload))
            .pipe(map(getNotCalculatedStepsAsync.success), catchError((message: string) => of(getNotCalculatedStepsAsync.failure(message))))
        )
    );
};

export const saveCouponEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(saveCoupon.request)),
        switchMap(action => from(api.postCoupon(action.payload))
            .pipe(map(saveCoupon.success), catchError((message: string) => of(saveCoupon.failure(message))))
        )
    );
};

export const loadPlayerInfoEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getPlayerInfoAsync.request)),
        switchMap(action => from(api.getPlayerInfo(action.payload))
            .pipe(map(getPlayerInfoAsync.success), catchError((message: string) => of(getPlayerInfoAsync.failure(message))))
        )
    );
};

export const loadPlayerSettingsEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getPlayerSettings.request)),
        switchMap(action => from(api.getPlayerSettings(action.payload))
            .pipe(map(getPlayerSettings.success), catchError((message: string) => of(getPlayerSettings.failure(message))))
        )
    );
};

export const savePlayerSettingsEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(savePlayerSettingsAsync.request)),
        switchMap(action => from(api.savePlayerSettings(action.payload))
            .pipe(map(savePlayerSettingsAsync.success), catchError((message: string) => of(savePlayerSettingsAsync.failure(message))))
        )
    );
};

export const getFavoritesTreeEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getFavoritesTreeAsync.request)),
        switchMap(() => from(api.getFavoritesTree())
            .pipe(map(getFavoritesTreeAsync.success), catchError((message: string) => of(getFavoritesTreeAsync.failure(message))))
        )
    );
};

export const getTreeLineEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getTreeFilterAsync.request)),
        switchMap(action => from(api.getTreeFilter(action.payload))
            .pipe(map(getTreeFilterAsync.success), catchError((message: string) => of(getTreeFilterAsync.failure(message))))
        )
    );
};

export const getTreeResultEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getTreeResultAsync.request)),
        switchMap(action => from(api.getTreeResult(action.payload))
            .pipe(map(getTreeResultAsync.success), catchError((message: string) => of(getTreeResultAsync.failure(message))))
        )
    );
};

export const getTreeLiveEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getTreeLiveAsync.request)),
        switchMap(action => from(api.getTreeLive(action.payload))
            .pipe(map(getTreeLiveAsync.success), catchError((message: string) => of(getTreeLiveAsync.failure(message))))
        )
    );
};

export const treePollingEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, { api }) => {
    return action$.pipe(
        filter(isActionOf(startTreePolling)),
        switchMap(action => {
            const {type, liveType, time} = action.payload;

            const request$ = () => {
                switch (type) {
                    case 'PREMATCH':
                        return from(api.getPrematchTree(time));
                    case 'RATING_PREMATCH':
                        return from(api.getRatingTree(time));
                    case 'LIVE':
                        return from(api.getTreeLive({ type: liveType }));
                    default:
                        return EMPTY;
                }
            };
            
            const treeSuccess = (type: TreeType, response: void | FlatTree) => {
                switch (type) {
                    case 'PREMATCH':
                        return getAllTreeAsync.success(response);
                    case 'RATING_PREMATCH':
                        return getTreeRatingAsync.success(response);
                    case 'LIVE':
                        return getTreeLiveAsync.success(response);
                   default:
                        return null;
                }
            };

            const treeFailure = (type: TreeType, err: any) => {
                switch (type) {
                    case 'PREMATCH':
                        return getAllTreeAsync.failure(err);
                    case 'RATING_PREMATCH':
                        return getTreeRatingAsync.failure(err);
                    case 'LIVE':
                        return getTreeLiveAsync.failure(err);
                    default:
                        return null;
                }
            };

            const isAction = (a: RootAction | null): a is RootAction => a !== null;

            return concat(
                request$().pipe(
                    map(response => treeSuccess(type, response)),
                    filter(isAction),
                    catchError(err => {
                        const action = treeFailure(type, err);
                        return action ? of(action) : EMPTY;
                    })
                ),

                interval(3000).pipe(
                    switchMap(() =>
                        request$().pipe(
                            map(response => treeSuccess(type, response)),
                            filter(isAction),
                            catchError(err => {
                                const action = treeFailure(type, err);
                                return action ? of(action) : EMPTY;
                            })
                        )
                    )
                )
            ).pipe(takeUntil(action$.pipe(filter(isActionOf(stopTreePolling)))));
        })
    );
}


export const getAllTreeEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getAllTreeAsync.request)),
        switchMap((action) => from(api.getPrematchTree(action.payload as any))
            .pipe(map(getAllTreeAsync.success), catchError((message: string) => of(getAllTreeAsync.failure(message))))
        )
    );
};

export const getTreeRatingEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getTreeRatingAsync.request)),
        switchMap((action) => from(api.getRatingTree(action.payload))
            .pipe(map(getTreeRatingAsync.success), catchError((message: string) => of(getTreeRatingAsync.failure(message))))
        )
    );
};

export const getBannerHostPortEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getBannerHostPortAsync.request)),
        switchMap(action => from(api.getBannerHostPort(action.payload))
            .pipe(map(getBannerHostPortAsync.success), catchError((message: string) => of(getBannerHostPortAsync.failure(message))))
        )
    );
};

export const getMarketLimitEpic: Epic<RootAction, RootAction, RootState, Services> = (action$, state$, {api}) => {
    return action$.pipe(
        filter(isActionOf(getMarketLimitAsync.request)),
        switchMap(action => from(api.getMarketLimit(action.payload))
            .pipe(map(getMarketLimitAsync.success), catchError((message: string) => of(getMarketLimitAsync.failure(message))))
        )
    );
};
